/**
 * a11y-dialog.js — minimal accessible-dialog helper for the theme's
 * overlays (cart drawer, monogram drawers, option-help drawer, popups).
 *
 * Usage:
 *   window.a11yDialog.open(dialogEl, { onClose: fn, initialFocus: el|selector, label: 'Cart' });
 *   window.a11yDialog.close(dialogEl);
 *
 * What it does (WCAG 2.1 AA — 2.1.1, 2.1.2, 2.4.3, 4.1.2):
 *   - sets role="dialog" + aria-modal="true" (+ aria-label if none present)
 *   - moves focus into the dialog on open, and back to the opener on close
 *   - traps Tab / Shift+Tab inside the dialog while it is open
 *   - closes on Esc (by calling the onClose callback you supply, so the
 *     existing show/hide code keeps doing the visual work)
 *   - hides the rest of the page from assistive tech with aria-hidden
 */
(function () {
  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

  var stack = []; // support nested dialogs

  function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  function focusables(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isVisible);
  }

  function siblingsOf(el) {
    // top-level children of <body> that are not ancestors of the dialog
    var out = [];
    var kids = document.body.children;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k === el || k.contains(el)) continue;
      if (k.tagName === 'SCRIPT' || k.tagName === 'STYLE') continue;
      out.push(k);
    }
    return out;
  }

  function open(el, opts) {
    if (!el) return;
    opts = opts || {};
    // already open?
    for (var i = 0; i < stack.length; i++) if (stack[i].el === el) return;

    var entry = {
      el: el,
      opener: opts.opener || document.activeElement,
      onClose: opts.onClose || function () {},
      hidden: [],
      keyHandler: null
    };

    el.setAttribute('role', el.getAttribute('role') || 'dialog');
    el.setAttribute('aria-modal', 'true');
    if (opts.label && !el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
      el.setAttribute('aria-label', opts.label);
    }
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');

    // hide background from AT
    siblingsOf(el).forEach(function (s) {
      if (s.getAttribute('aria-hidden') === 'true') return;
      s.setAttribute('aria-hidden', 'true');
      s.setAttribute('data-a11y-dialog-hidden', '');
      entry.hidden.push(s);
    });

    entry.keyHandler = function (e) {
      if (stack[stack.length - 1] !== entry) return; // only the top dialog handles keys
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        e.stopPropagation();
        entry.onClose();
        close(el);
        return;
      }
      if (e.key === 'Tab') {
        var f = focusables(el);
        if (!f.length) { e.preventDefault(); el.focus(); return; }
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener('keydown', entry.keyHandler, true);
    stack.push(entry);

    // initial focus (after any CSS transition kicks in)
    window.setTimeout(function () {
      var target = null;
      if (typeof opts.initialFocus === 'string') target = el.querySelector(opts.initialFocus);
      else if (opts.initialFocus) target = opts.initialFocus;
      if (!target) {
        var f = focusables(el);
        target = f.length ? f[0] : el;
      }
      try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
    }, 50);
  }

  function close(el) {
    for (var i = stack.length - 1; i >= 0; i--) {
      if (stack[i].el !== el) continue;
      var entry = stack.splice(i, 1)[0];
      document.removeEventListener('keydown', entry.keyHandler, true);
      entry.hidden.forEach(function (s) {
        s.removeAttribute('aria-hidden');
        s.removeAttribute('data-a11y-dialog-hidden');
      });
      el.removeAttribute('aria-modal');
      if (entry.opener && typeof entry.opener.focus === 'function' && document.contains(entry.opener)) {
        try { entry.opener.focus({ preventScroll: true }); } catch (err) { entry.opener.focus(); }
      }
      return;
    }
  }

  function isOpen(el) {
    for (var i = 0; i < stack.length; i++) if (stack[i].el === el) return true;
    return false;
  }

  window.a11yDialog = { open: open, close: close, isOpen: isOpen };
})();
